import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CallSplit as SplitterIcon,
  Input as InputIcon,
  Output as OutputIcon,
  CenterFocusStrong as CenterIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import ReactFlow, {
  Background,
  Controls,
  Node,
  Edge,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';

import { splitterService, ReactFlowNode, ReactFlowEdge } from '../../services/splitterService';
import { FtthCaixa } from '../../services/ftthCaixaService';
import { Cabo, caboService } from '../../services/caboService';
import SplitterNode from '../flow/nodes/SplitterNode';
import InputNode from '../flow/nodes/InputNode';
import OutputNode from '../flow/nodes/OutputNode';
import CaboNode from '../flow/nodes/CaboNode';
import CaboPassagemNode from '../flow/nodes/CaboPassagemNode';
import CaboSelectionModal from './CaboSelectionModal';
import CabosForm from '../forms/FtthCabosForm';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import DeletableEdge from '../flow/edges/DeletableEdge';
import BiDirectionalEdge from '../flow/edges/BiDirectionalEdge';
import { validateConnection, shouldUseBidirectionalEdge } from '../../utils/connectionValidation';
import { applySignalsToState, applySignalsToNodes, applySignalsToEdges } from '../../utils/diagramSignalsUtils';
import { exportDiagramImage } from '../../utils/exportDiagramImage';
import SplitterAddModal, { SplitterAddConfig } from './SplitterAddModal';
import SplitterEditPadraoModal, { SplitterEditConfig } from './SplitterEditPadraoModal';
import type { PadraoCoresSplitter } from '../../utils/fiberColors';

// Tipos de nós customizados
const nodeTypes = {
  splitter: SplitterNode,
  caboIn: CaboNode, // Cabos normais (entrada)
  caboOut: CaboPassagemNode, // Cabos de passagem (saída)
  input: InputNode,
  output: OutputNode,
};

// Tipos de edges customizados
const edgeTypes = {
  deletable: DeletableEdge,
  bidirectional: BiDirectionalEdge,
};

export interface CaixaDiagramModalProps {
  open: boolean;
  onClose: () => void;
  caixa: FtthCaixa;
  isFullscreen?: boolean;
  /** Quando em tela cheia, passar o elemento container para o modal aparecer sobre o mapa */
  container?: HTMLElement | null;
}

// ✅ Componente memoizado para evitar re-renders desnecessários
const CaixaDiagramContent = React.memo<{
  caixa: FtthCaixa;
  onClose: () => void;
  isFullscreen?: boolean;
  /** Quando em tela cheia, container para o Menu aparecer dentro do elemento fullscreen */
  container?: HTMLElement | null;
}>(({ caixa, onClose, isFullscreen = false, container }) => {
  // Estados para React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Estados da UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Estados do menu de adicionar
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  // Estado para edges selecionadas (para deletar com tecla Delete)
  const [selectedEdges, setSelectedEdges] = useState<string[]>([]);
  const [refreshingSignal, setRefreshingSignal] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const [flowInteractive, setFlowInteractive] = useState(false);
  const [showCabosForm, setShowCabosForm] = useState(false);
  const [editingCabo, setEditingCabo] = useState<Cabo | null>(null);
  const [showSplitterAddModal, setShowSplitterAddModal] = useState(false);
  const [showSplitterEditModal, setShowSplitterEditModal] = useState(false);
  const [editingSplitter, setEditingSplitter] = useState<{
    id: number;
    padrao_cores: PadraoCoresSplitter;
    config: string;
    is1x2: boolean;
    saida1_porcentagem: number;
    saida2_porcentagem: number;
  } | null>(null);

  // ✅ Refs para evitar dependências desnecessárias
  const edgesRef = useRef(edges);
  const nodesRef = useRef(nodes);
  const loadingRef = useRef(false); // Prevenir múltiplas chamadas simultâneas
  const flowContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    setFlowInteractive(false);
  }, [caixa.id]);

  const reactFlowInstance = useReactFlow();

  /** Busca sinais ópticos e aplica aos nós/edges (único ponto de chamada a getOpticalSignals para atualização). */
  const refreshSignalsSilent = useCallback(async () => {
    const signalsData = await splitterService.getOpticalSignals(caixa.id, 'caixa');
    applySignalsToState(signalsData, setNodes, setEdges);
  }, [caixa.id, setNodes, setEdges]);

  // ✅ Callbacks estáveis com refs para evitar dependências circulares
  const handleDeleteConnection = useCallback(async (edgeId: string) => {
    try {
      // Encontrar a edge no estado atual para obter o ID correto
      const edgeToDelete = edgesRef.current.find(edge => edge.id === edgeId);

      if (!edgeToDelete) {
        toast.error('Conexão não encontrada');
        return;
      }

      // Usar o ID do backend se disponível, senão extrair do ID da edge
      let backendId: number;

      if (edgeToDelete.data?.id && typeof edgeToDelete.data.id === 'number') {
        backendId = edgeToDelete.data.id;
      } else {
        const numericId = edgeId.replace(/^.*-/, '');
        backendId = parseInt(numericId);

        if (isNaN(backendId) || backendId === 0) {
          toast.error('ID da conexão inválido. Recarregue a página e tente novamente.');
          return;
        }
      }

      // Deletar conexão no backend
      await splitterService.deleteConnection(backendId);

      // Remover edge localmente
      setEdges((eds) => eds.filter(edge => edge.id !== edgeId));

      toast.success('Conexão deletada com sucesso!');

      try {
        await refreshSignalsSilent();
      } catch (_err) {
      }

    } catch (error: any) {
      toast.error(`Erro ao deletar conexão: ${error.message || 'Erro desconhecido'}`);
    }
  }, [setEdges, refreshSignalsSilent]);

  const handleDeleteSplitter = useCallback(async (splitterId: number) => {
    try {
      const { hasSiblings, siblingIds } = await splitterService.getCaboSiblings(splitterId);

      let deletePair = false;
      if (hasSiblings && siblingIds.length > 0) {
        const result = await Swal.fire({
          title: 'Existe outro nó deste cabo',
          html: 'Há outro nó (entrada ou saída) do mesmo cabo nesta caixa. Deseja deletar apenas este ou ambos?<br/><small>Se deletar ambos, o vínculo com o ponto no mapa será removido.</small>',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Deletar apenas este',
          denyButtonText: 'Deletar ambos',
          cancelButtonText: 'Cancelar',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: { container: 'swal-zindex', popup: 'swal-zindex' },
          didOpen: () => {
            const el = document.querySelector('.swal2-container') as HTMLElement;
            if (el) el.style.zIndex = '20001';
          }
        });
        if (result.isDismissed) return;
        if (result.isDenied) deletePair = true;
      } else {
        const confirmed = await Swal.fire({
          title: 'Confirmar exclusão',
          text: 'Tem certeza que deseja deletar este item? Esta ação não pode ser desfeita.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, deletar',
          cancelButtonText: 'Cancelar',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: { container: 'swal-zindex', popup: 'swal-zindex' },
          didOpen: () => {
            const el = document.querySelector('.swal2-container') as HTMLElement;
            if (el) el.style.zIndex = '20001';
          }
        });
        if (!confirmed.isConfirmed) return;
      }

      await splitterService.deleteSplitter(splitterId, deletePair);
      const idsToRemove = deletePair ? [splitterId, ...siblingIds] : [splitterId];
      const idsSet = new Set(idsToRemove);

      const isEdgeConnectedToDeleted = (edge: { source: string; target: string }) => {
        const srcNum = parseInt(edge.source.replace(/\D/g, ''), 10);
        const tgtNum = parseInt(edge.target.replace(/\D/g, ''), 10);
        return idsSet.has(srcNum) || idsSet.has(tgtNum);
      };

      setNodes((currentNodes) => currentNodes.filter(node => !idsSet.has(node.data.id)));
      setEdges((currentEdges) => currentEdges.filter(edge => !isEdgeConnectedToDeleted(edge)));

      toast.success(deletePair ? 'Cabo removido e vínculo desfeito!' : 'Deletado com sucesso!');

    } catch (error: any) {
      toast.error(`Erro ao deletar splitter: ${error.message || 'Erro desconhecido'}`);
    }
  }, [setNodes, setEdges]);

  const handleUpdatePorcentagem = useCallback(async (splitterId: number, porcentagem: number) => {
    setNodes((currentNodes) => currentNodes.map(node =>
      node.data?.id === splitterId
        ? {
          ...node,
          data: {
            ...node.data,
            saida1_porcentagem: porcentagem,
            saida2_porcentagem: 100 - porcentagem
          }
        }
        : node
    ));

    try {
      const result = await splitterService.updateSaida1Porcentagem(splitterId, porcentagem);

      if (result && (result.saida1_porcentagem !== porcentagem || result.saida2_porcentagem !== (100 - porcentagem))) {
        setNodes((currentNodes) => currentNodes.map(node =>
          node.data?.id === splitterId
            ? {
              ...node,
              data: {
                ...node.data,
                saida1_porcentagem: result.saida1_porcentagem,
                saida2_porcentagem: result.saida2_porcentagem,
                config_json: result.config_json
              }
            }
            : node
        ));

        toast.success(`Porcentagem atualizada: ${result.saida1_porcentagem}% / ${result.saida2_porcentagem}%`);
      } else {
        toast.success(`Porcentagem atualizada: ${porcentagem}% / ${100 - porcentagem}%`);
      }

      try {
        await refreshSignalsSilent();
      } catch (_err) {
      }

    } catch (error: any) {
      toast.warning(`Porcentagem atualizada localmente (${porcentagem}% / ${100 - porcentagem}%), mas houve erro ao salvar no servidor.`);
    }
  }, [setNodes, caixa.id, refreshSignalsSilent]);

  const handleUpdateObservacao = useCallback(async (splitterId: number, observacao: string) => {
    setNodes((currentNodes) => currentNodes.map(node =>
      node.data?.id === splitterId
        ? {
          ...node,
          data: {
            ...node.data,
            observacao: observacao
          }
        }
        : node
    ));

    try {
      await splitterService.updateSplitterObservacao(splitterId, observacao);
      toast.success('Observação atualizada com sucesso!');

    } catch (error: any) {
      toast.warning('Observação atualizada localmente, mas houve erro ao salvar no servidor.');
    }
  }, [setNodes]);

  const handleUpdatePortaTipo = useCallback(async (splitterId: number, numeroFibra: number, tipo: 'in' | 'out') => {
    try {
      const { portas_tipo } = await splitterService.updatePortaTipo(splitterId, numeroFibra, tipo);
      setNodes((currentNodes) => currentNodes.map(node => {
        if (node.data?.id !== splitterId) return node;
        return { ...node, data: { ...node.data, portas_tipo } };
      }));
      toast.success('Tipo da porta atualizado.');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao atualizar tipo da porta.');
    }
  }, [setNodes]);

  const handleEditCabo = useCallback(async (caboId: number) => {
    try {
      const cabo = await caboService.getCaboById(caboId);
      setEditingCabo(cabo);
      setShowCabosForm(true);
    } catch (error: any) {
      toast.error(`Erro ao carregar cabo: ${error.message || 'Erro desconhecido'}`);
    }
  }, []);

  const applyCaboUpdateToNodes = useCallback((cabo: Cabo) => {
    setNodes((currentNodes) => currentNodes.map((node) => {
      if (node.type !== 'caboIn' && node.type !== 'caboOut') return node;
      if (node.data?.cabo_id !== cabo.id) return node;

      const defaultTipoPorta = node.type === 'caboOut' ? 'in' : 'out';
      const existingPortas = (node.data.portas_tipo ?? {}) as Record<string, 'in' | 'out'>;
      const portas_tipo: Record<string, 'in' | 'out'> = {};
      for (let i = 1; i <= cabo.qtde_fo; i++) {
        portas_tipo[String(i)] = existingPortas[String(i)] ?? defaultTipoPorta;
      }

      return {
        ...node,
        data: {
          ...node.data,
          titulo: cabo.titulo,
          qtde_fo: cabo.qtde_fo,
          color: cabo.color,
          cabo_tipo: cabo.tipo ?? null,
          padrao_tubo_loose: cabo.padrao_tubo_loose ?? 'abnt',
          qtde_tubo_loose: cabo.qtde_tubo_loose ?? 1,
          portas_tipo,
        },
      };
    }));
  }, [setNodes]);

  const handleCabosFormSuccess = useCallback((cabo: Cabo) => {
    applyCaboUpdateToNodes(cabo);
    setShowCabosForm(false);
    setEditingCabo(null);
    toast.success('Cabo atualizado com sucesso!');
  }, [applyCaboUpdateToNodes]);

  const handleCabosFormCancel = useCallback(() => {
    setShowCabosForm(false);
    setEditingCabo(null);
  }, []);

  const handleEditSplitterPadrao = useCallback((splitterId: number) => {
    const node = nodesRef.current.find((n) => n.data?.id === splitterId && n.type === 'splitter');
    if (!node) return;
    const outputs = typeof node.data.outputs === 'number' ? node.data.outputs : parseInt(node.data.outputs) || 2;
    setEditingSplitter({
      id: splitterId,
      padrao_cores: (node.data.padrao_cores as PadraoCoresSplitter) ?? 'sem_padrao',
      config: node.data.config ?? `1x${outputs}`,
      is1x2: outputs === 2,
      saida1_porcentagem: node.data.saida1_porcentagem ?? 50,
      saida2_porcentagem: node.data.saida2_porcentagem ?? 50,
    });
    setShowSplitterEditModal(true);
  }, []);

  const handleSaveSplitterPadrao = useCallback(async (data: SplitterEditConfig) => {
    if (!editingSplitter) return;
    try {
      await splitterService.updateSplitter(editingSplitter.id, { padrao_cores: data.padrao_cores } as any);

      let saida1 = data.saida1_porcentagem;
      let saida2 = data.saida2_porcentagem;

      if (editingSplitter.is1x2 && saida1 !== undefined) {
        const result = await splitterService.updateSaida1Porcentagem(editingSplitter.id, saida1);
        saida1 = result.saida1_porcentagem;
        saida2 = result.saida2_porcentagem;
      }

      setNodes((current) => current.map((node) => {
        if (node.data?.id !== editingSplitter.id || node.type !== 'splitter') return node;
        return {
          ...node,
          data: {
            ...node.data,
            padrao_cores: data.padrao_cores,
            ...(editingSplitter.is1x2 && saida1 !== undefined
              ? { saida1_porcentagem: saida1, saida2_porcentagem: saida2 }
              : {}),
          },
        };
      }));
      setShowSplitterEditModal(false);
      setEditingSplitter(null);
      toast.success('Splitter atualizado!');

      if (editingSplitter.is1x2 && data.saida1_porcentagem !== undefined) {
        try {
          await refreshSignalsSilent();
        } catch (_err) {
          // sinal será recalculado na próxima atualização manual
        }
      }
    } catch (error: any) {
      toast.error(`Erro ao atualizar splitter: ${error.message || 'Erro desconhecido'}`);
    }
  }, [editingSplitter, setNodes, refreshSignalsSilent]);

  // ✅ Função de carregamento otimizada e memoizada
  const loadCaixaData = useCallback(async () => {
    // ✅ Prevenir múltiplas chamadas simultâneas
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;

    try {
      setLoading(true);
      setError('');

      // Carregar nós, edges e sinais em paralelo
      const [nodesData, edgesData, signalsData] = await Promise.all([
        splitterService.getNodesForReactFlow(caixa.id),
        splitterService.getEdgesForReactFlow(caixa.id),
        splitterService.getOpticalSignals(caixa.id, 'caixa')
      ]);

      // Processar nós (opticalSignal aplicado via util unificado)
      const baseNodes = (nodesData && Array.isArray(nodesData.nodes)) ? nodesData.nodes
        .filter(node => node && node.data)
        .map(node => ({
          ...node,
          data: {
            ...node.data,
            outputs: typeof node.data.outputs === 'number' ? node.data.outputs : parseInt(node.data.outputs) || 2,
            saida1_porcentagem: (node.data as any).saida1_porcentagem || 50,
            saida2_porcentagem: (node.data as any).saida2_porcentagem || 50,
            opticalSignal: null as any,
            onDelete: handleDeleteSplitter,
            onUpdatePorcentagem: handleUpdatePorcentagem,
            onUpdateObservacao: handleUpdateObservacao,
            onUpdatePortaTipo: handleUpdatePortaTipo,
            onEditCabo: handleEditCabo,
            onEditSplitterPadrao: handleEditSplitterPadrao,
          }
        })) : [];
      const processedNodes = applySignalsToNodes(baseNodes, signalsData);

      // Processar edges (animated = sinal do cabo, via util unificado)
      const baseEdges = Array.isArray(edgesData) ? edgesData.map(edge => ({
        ...edge,
        type: 'deletable',
        animated: false,
        data: {
          ...edge.data,
          onDelete: handleDeleteConnection
        }
      })) : [];
      const processedEdges = applySignalsToEdges(baseEdges, signalsData);

      const edgesWithSignal = processedEdges.filter(e => e.animated).length;

      // ✅ Atualizar estados em batch
      setNodes(processedNodes);
      setEdges(processedEdges);

      // Auto-fit após carregar
      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.fitView({ padding: 0.2 });
        }
      }, 100);

    } catch (err: any) {
      setError(`Erro ao carregar diagrama: ${err.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [caixa.id, handleDeleteSplitter, handleUpdatePorcentagem, handleUpdateObservacao, handleUpdatePortaTipo, handleEditCabo, handleEditSplitterPadrao, handleDeleteConnection, reactFlowInstance, setNodes, setEdges]);

  // ✅ Effect otimizado - só executa quando necessário
  useEffect(() => {
    if (caixa.id && !loadingRef.current) {
      loadCaixaData();
    }
  }, [caixa.id]); // ✅ Dependência mínima

  // Handler para seleção de edges
  const onSelectionChange = useCallback((params: { nodes: Node[]; edges: Edge[] }) => {
    setSelectedEdges(params.edges.map(edge => edge.id));
  }, []);

  // Handler para tecla Delete
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' && selectedEdges.length > 0) {
        event.preventDefault();
        selectedEdges.forEach(edgeId => {
          handleDeleteConnection(edgeId);
        });

        setSelectedEdges([]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedEdges, handleDeleteConnection]);

  // ✅ Handlers memoizados para evitar re-renders
  const onConnect = useCallback(
    async (params: Connection) => {
      try {
        if (!params.source || !params.target) {
          return;
        }

        const edgeType = shouldUseBidirectionalEdge(params) ? 'bidirectional' : 'deletable';
        const edgeColor = '#2196f3';

        const connectionData = {
          caixa_id: caixa.id,
          source: params.source,
          sourceHandle: params.sourceHandle || undefined,
          target: params.target,
          targetHandle: params.targetHandle || undefined,
          connection_label: '',
          style_color: edgeColor
        };

        const createdConnection = await splitterService.createConnection(connectionData);

        const newEdge = {
          id: `connection-${createdConnection.id}`,
          source: params.source!,
          sourceHandle: params.sourceHandle || undefined,
          target: params.target!,
          targetHandle: params.targetHandle || undefined,
          type: edgeType,
          animated: false,
          label: '',
          labelStyle: { fontSize: 11, fontWeight: 'bold' },
          style: { stroke: edgeColor, strokeWidth: 2 },
          data: {
            id: createdConnection.id,
            caixa_id: caixa.id,
            onDelete: handleDeleteConnection
          }
        };

        setEdges((eds) => [...eds, newEdge]);

        toast.success('Conexão criada com sucesso!');

        // Atualizar sinais na próxima microtask para não sobrescrever a edge recém-adicionada
        setTimeout(() => {
          refreshSignalsSilent().catch(() => {});
        }, 0);
      } catch (error: any) {
        toast.error(`Erro ao criar conexão: ${error.message || 'Erro desconhecido'}`);
      }
    },
    [caixa.id, setEdges, handleDeleteConnection, refreshSignalsSilent]
  );

  const onNodeDragStop = useCallback(
    async (event: React.MouseEvent, node: Node) => {
      try {
        let itemId: number | null = null;

        if (node.id.startsWith('splitter-')) {
          itemId = parseInt(node.id.replace('splitter-', ''));
        } else if (node.id.startsWith('cabo-')) {
          itemId = parseInt(node.id.replace('cabo-', ''));
        }

        if (itemId && !isNaN(itemId)) {
          await splitterService.updateSplitterPosition(itemId, node.position);
        }

      } catch (error: any) {
        toast.error(`Erro ao salvar posição: ${error.message || 'Erro desconhecido'}`);
      }
    },
    []
  );

  // ✅ Handlers do menu memoizados
  const handleAddMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  }, []);

  const handleAddMenuClose = useCallback(() => {
    setAddMenuAnchor(null);
  }, []);

  const handleOpenSplitterModal = useCallback(() => {
    setShowSplitterAddModal(true);
    handleAddMenuClose();
  }, [handleAddMenuClose]);

  const handleCenterView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ padding: 0.2 });
    }
  }, [reactFlowInstance]);

  /** Recalcula o sinal óptico das conexões e atualiza a animação dos cabos */
  const handleRefreshSignal = useCallback(async () => {
    if (refreshingSignal) return;
    setRefreshingSignal(true);
    try {
      await refreshSignalsSilent();
      toast.success('Sinal das conexões atualizado');
    } catch (_err: any) {
      toast.error('Erro ao recalcular sinal. Tente novamente.');
    } finally {
      setRefreshingSignal(false);
    }
  }, [refreshingSignal, refreshSignalsSilent]);

  const handleExportImage = useCallback(async () => {
    if (!reactFlowInstance || !flowContainerRef.current || exportingImage) return;
    setExportingImage(true);
    try {
      await exportDiagramImage(
        flowContainerRef.current,
        reactFlowInstance,
        `diagrama_caixa_${caixa.name}`,
      );
      toast.success('Imagem exportada com sucesso!');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao exportar imagem');
    } finally {
      setExportingImage(false);
    }
  }, [reactFlowInstance, exportingImage, caixa.name]);

  // ✅ Função para criar nó React Flow memoizada
  const createReactFlowNode = useCallback((splitter: any): ReactFlowNode => {
    let processedConfigSaidas = splitter.config_saidas;

    if (typeof splitter.config_saidas === 'string') {
      try {
        processedConfigSaidas = JSON.parse(splitter.config_saidas);
      } catch {
        processedConfigSaidas = null;
      }
    }

    let outputs: number;

    if (typeof processedConfigSaidas === 'object' && processedConfigSaidas !== null) {
      const saidaKeys = Object.keys(processedConfigSaidas).filter((k) => /^saida\d+$/.test(k));
      outputs = saidaKeys.length > 0 ? saidaKeys.length : 2;
    } else if (typeof processedConfigSaidas === 'number') {
      outputs = processedConfigSaidas;
    } else {
      outputs = 2;
    }

    const is1x2 = outputs === 2;
    let padraoCores: PadraoCoresSplitter = 'sem_padrao';
    if (typeof processedConfigSaidas === 'object' && processedConfigSaidas !== null) {
      const p = (processedConfigSaidas as { padrao_cores?: PadraoCoresSplitter }).padrao_cores;
      if (p === 'abnt' || p === 'eia598a' || p === 'sem_padrao') padraoCores = p;
    }

    const baseData = {
      id: splitter.id,
      label: `Splitter ${splitter.id}`,
      tipo: splitter.tipo,
      config: `1x${outputs}`,
      color: '#2196f3',
      inputs: 1,
      outputs: outputs,
      padrao_cores: (splitter as any).padrao_cores ?? padraoCores,
      onDelete: handleDeleteSplitter,
      onUpdatePorcentagem: handleUpdatePorcentagem,
      onEditSplitterPadrao: handleEditSplitterPadrao,
    };

    if (is1x2 && typeof processedConfigSaidas === 'object' &&
      processedConfigSaidas.saida1?.valor !== undefined &&
      processedConfigSaidas.saida2?.valor !== undefined) {
      return {
        id: `splitter-${splitter.id}`,
        type: 'splitter',
        position: { x: splitter.posicao_x, y: splitter.posicao_y },
        data: {
          ...baseData,
          saida1_porcentagem: processedConfigSaidas.saida1.valor,
          saida2_porcentagem: processedConfigSaidas.saida2.valor,
          config_json: processedConfigSaidas
        } as any
      };
    }

    return {
      id: `splitter-${splitter.id}`,
      type: 'splitter',
      position: { x: splitter.posicao_x, y: splitter.posicao_y },
      data: baseData as any
    };
  }, [handleDeleteSplitter, handleUpdatePorcentagem, handleEditSplitterPadrao]);

  // ✅ Handlers de adição memoizados
  const handleAddSplitter = useCallback(async ({ saidas, padrao_cores, saida1_porcentagem, saida2_porcentagem }: SplitterAddConfig) => {
    try {
      const splitterData: Record<string, unknown> = {
        caixa_id: caixa.id,
        tipo: 'splitter',
        posicao_x: 200,
        posicao_y: 200,
        config_saidas: saidas,
        padrao_cores,
      };
      if (saidas === 2) {
        splitterData.saida1_porcentagem = saida1_porcentagem;
        splitterData.saida2_porcentagem = saida2_porcentagem;
      }
      const newSplitter = await splitterService.createSplitter(splitterData as any);
      const newNode = createReactFlowNode(newSplitter);
      setNodes((currentNodes) => [...currentNodes, newNode]);

      toast.success(`Splitter 1x${saidas} adicionado!`);

    } catch (error: any) {
      toast.error(`Erro ao adicionar splitter: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setShowSplitterAddModal(false);
    }
  }, [caixa.id, createReactFlowNode, setNodes]);


  const nodeColorFunction = useCallback((node: Node) => {
    if (node.type === 'splitter') return '#2196f3';
    if (node.type === 'input') return '#4CAF50';
    if (node.type === 'output') return '#FF9800';
    return '#999';
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2">Carregando diagrama...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 1, py: 1 }}>
          {error}
        </Alert>
        <Button variant="outlined" size="small" onClick={loadCaixaData} startIcon={<RefreshIcon />}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box ref={flowContainerRef} sx={{ height: '100%', minHeight: 400, width: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        isValidConnection={(c) => validateConnection(c, edges)}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        nodesDraggable={flowInteractive}
        nodesConnectable={flowInteractive}
        elementsSelectable={flowInteractive}
        edgesUpdatable={flowInteractive}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        onSelectionChange={onSelectionChange}
      >
        <Background />
        <Controls
          showFitView={false}
          onInteractiveChange={setFlowInteractive}
        />

        <Panel position="top-right">
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={exportingImage ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
            onClick={handleExportImage}
            disabled={exportingImage || nodes.length === 0}
            sx={{ boxShadow: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {exportingImage ? 'Exportando...' : 'Exportar imagem'}
          </Button>
        </Panel>

        <Panel position="top-left">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Tooltip
              title={flowInteractive ? 'Adicionar Componente' : 'Desbloqueie o diagrama para adicionar componentes'}
              placement="right"
            >
              <span>
                <Fab
                  size="small"
                  color="primary"
                  onClick={handleAddMenuOpen}
                  disabled={!flowInteractive}
                >
                  <AddIcon />
                </Fab>
              </span>
            </Tooltip>

            <Tooltip title="Centralizar Diagrama" placement="right">
              <Fab
                size="small"
                onClick={handleCenterView}
              >
                <CenterIcon />
              </Fab>
            </Tooltip>

            <Tooltip title="Recalcular sinal das conexões" placement="right">
              <span>
                <Fab
                  size="small"
                  color="default"
                  onClick={handleRefreshSignal}
                  disabled={refreshingSignal}
                >
                  {refreshingSignal ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                </Fab>
              </span>
            </Tooltip>
          </Box>
        </Panel>
      </ReactFlow>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        container={isFullscreen && container ? container : undefined}
        sx={{
          zIndex: isFullscreen ? 10002 : 1400,
        }}
      >

        <MenuItem onClick={handleOpenSplitterModal}>
          <ListItemIcon>
            <SplitterIcon />
          </ListItemIcon>
          <ListItemText primary="Splitter" secondary="1x2, 1x4, 1x8, 1x16 ou 1x32" />
        </MenuItem>
      </Menu>

      <SplitterAddModal
        open={showSplitterAddModal}
        onClose={() => setShowSplitterAddModal(false)}
        onConfirm={handleAddSplitter}
        isFullscreen={isFullscreen}
        container={container}
      />

      <SplitterEditPadraoModal
        open={showSplitterEditModal}
        onClose={() => {
          setShowSplitterEditModal(false);
          setEditingSplitter(null);
        }}
        onConfirm={handleSaveSplitterPadrao}
        padraoAtual={editingSplitter?.padrao_cores}
        splitterConfig={editingSplitter?.config}
        is1x2={editingSplitter?.is1x2}
        saida1Atual={editingSplitter?.saida1_porcentagem}
        saida2Atual={editingSplitter?.saida2_porcentagem}
        isFullscreen={isFullscreen}
        container={container}
      />

      <Dialog
        open={showCabosForm}
        onClose={handleCabosFormCancel}
        maxWidth="md"
        fullWidth
        container={isFullscreen && container ? container : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10004 : 1500 }}
      >
        <DialogContent>
          <CabosForm
            cabo={editingCabo || undefined}
            pontos={(editingCabo?.pontos ?? []).map((ponto) => ({
              lat: ponto.lat,
              lng: ponto.lng,
              id: ponto.id,
              caixa_armario_id: ponto.caixa_armario_id,
              caixa_armario_tipo: ponto.caixa_armario_tipo,
            }))}
            menuZIndex={isFullscreen ? 10005 : 1600}
            onSuccess={handleCabosFormSuccess}
            onCancel={handleCabosFormCancel}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
});

// ✅ Definir displayName para debugging
CaixaDiagramContent.displayName = 'CaixaDiagramContent';

// ✅ Componente principal também memoizado
const CaixaDiagramModal = React.memo<CaixaDiagramModalProps>(({ open, onClose, caixa, isFullscreen = false, container }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      container={container ?? undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{
        zIndex: isFullscreen ? 10000 : 1300,
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '80vh',
          zIndex: isFullscreen ? 10001 : 'auto',
        },
        '& .MuiDialogContent-root': { py: 0 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.5, pb: 1, px: 2 }}>
        <Box sx={{ '& .MuiTypography-root': { lineHeight: 1.3 } }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.25 }}>
            🗂️ Diagrama da Caixa - {caixa.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Documentação de fusões, splitters e conectividade
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mb: 0.5, mr: 0.5, ml: 0.5, p: 0, pt: 0, overflow: 'hidden' }}>
        <ReactFlowProvider>
          <CaixaDiagramContent caixa={caixa} onClose={onClose} isFullscreen={isFullscreen} container={container} />
        </ReactFlowProvider>
      </DialogContent>
    </Dialog>
  );
});

CaixaDiagramModal.displayName = 'CaixaDiagramModal';

export default CaixaDiagramModal; 